<script setup>
import { ref } from 'vue'
import TableList from '../others/TableList.vue'
import ActionTrigger from '@/features/data/presentation/components/ActionTrigger.vue'
import { EVENTS } from '@/shared/constants/commons'
import Events from '@/events/events'
import Commons from '@/shared/utils/commons'
import SlideDetailView from '../others/SlideDetailView.vue'
import { cloneDeep } from 'lodash-es'

// Reactive data
const detailData = ref(null)
const viewTicket = ref(false)
const updateTicketTrigger = ref(null)
const addMessageTrigger = ref(null)
const newMessage = ref('')
const isSendingMessage = ref(false)
const isUpdatingStatus = ref(false)

// Table columns
const cols = [
  {
    name: 'Sujet & Message',
    id: 'subject',
    value(el) {
      let subj = el.subject || el.title || '-'
      let msg = el.message || el.description || '-'
      if (msg.length > 30) msg = msg.substring(0, 30) + '...'
      return [subj, msg]
    },
  },
  {
    name: 'Émetteur',
    id: 'userName',
    value(el) {
      return el.userName || el.user?.fullName || el.profile?.name || el.customerName || 'Inconnu'
    },
  },
  {
    name: 'Date',
    id: 'createdAt',
    value(el) {
      return Commons.time(el.createdAt, 'DD MMM YYYY à HH:mm')
    },
  },
  // {
  //   name: 'Priorité',
  //   id: 'priority',
  //   filter: {
  //     key: 'priority',
  //     default: '',
  //     options: [
  //       { name: 'Tout', value: '' },
  //       { name: 'Basse', value: 'low' },
  //       { name: 'Normale', value: 'normal' },
  //       { name: 'Haute', value: 'high' },
  //       { name: 'Urgente', value: 'urgent' },
  //     ],
  //   },
  //   value(el) {
  //     const priorityMap = {
  //       low: '<span class="px-2 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-medium">Basse</span>',
  //       normal: '<span class="px-2 py-1 rounded-full bg-blue-100 text-blue-600 text-xs font-medium">Normale</span>',
  //       high: '<span class="px-2 py-1 rounded-full bg-orange-100 text-orange-600 text-xs font-medium">Haute</span>',
  //       urgent: '<span class="px-2 py-1 rounded-full bg-red-100 text-red-600 text-xs font-medium">Urgente</span>',
  //     }
  //     return (
  //       priorityMap[el.priority] ||
  //       '<span class="px-2 py-1 rounded-full bg-blue-100 text-blue-600 text-xs font-medium">Normale</span>'
  //     )
  //   },
  // },
  {
    name: 'Statut',
    id: 'status',
    // filter: {
    //   key: 'status',
    //   default: '',
    //   options: [
    //     { name: 'Tout', value: '' },
    //     { name: 'Ouvert', value: 'open' },
    //     { name: 'En cours', value: 'pending' },
    //     { name: 'Résolu', value: 'resolved' },
    //     { name: 'Fermé', value: 'closed' },
    //   ],
    // },
    value(el) {
      const statusMap = {
        open: '<span class="px-2 py-1 rounded-full bg-green-100 text-green-600 text-xs font-semibold">Ouvert</span>',
        pending: '<span class="px-2 py-1 rounded-full bg-yellow-100 text-yellow-600 text-xs font-semibold">En cours</span>',
        resolved: '<span class="px-2 py-1 rounded-full bg-blue-100 text-blue-600 text-xs font-semibold">Résolu</span>',
        closed: '<span class="px-2 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold">Fermé</span>',
      }
      return statusMap[el.status] || '-'
    },
  },
  {
    name: 'Actions',
    id: 'action',
    icon: () => '/images/icons/dots.png',
    options: el => {
      const actions = []
      const status = el.status || 'open'

      actions.push({
        name: 'Voir les détails',
        action: 'view',
        icon: 'pi pi-eye',
      })

      if (status === 'open') {
        actions.push({
          name: 'Marquer en cours',
          status: 'pending',
          icon: 'pi pi-play',
        })
      }
      
      if (status !== 'resolved' && status !== 'closed') {
        actions.push({
          name: 'Marquer comme résolu',
          status: 'resolved',
          icon: 'pi pi-check',
        })
      }

      if (status !== 'closed') {
        actions.push({
          name: 'Fermer le ticket',
          status: 'closed',
          icon: 'pi pi-times-circle',
        })
      }

      return actions
    },
    onClick(action, el) {
      if (action.action === 'view') {
        handleRowClick(el)
        return
      }
      handleStatusChangeFromTable(el, action.status)
    },
    value() {},
  },
]

// Methods
const handleRowClick = ticket => {
  detailData.value = cloneDeep(ticket)
  viewTicket.value = true
}

/**
 * Changement de statut depuis le tableau (dropdown)
 */
const handleStatusChangeFromTable = async (ticket, newStatus) => {
  try {
    await updateTicketTrigger.value.trigger({
      ticket: cloneDeep(ticket),
      newStatus,
    })
    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
      title: 'Succès',
      body: 'Statut mis à jour avec succès',
    })
    Events.dispatch('refresh_tickets')
  } catch {
    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
      title: 'Erreur',
      body: 'Impossible de mettre à jour le statut',
    })
  }
}

/**
 * Changement de statut depuis le panneau de détail — direct, sans modal
 */
const handleStatusChangeFromDetail = async (newStatus) => {
  if (!detailData.value || isUpdatingStatus.value) return
  isUpdatingStatus.value = true
  try {
    const result = await updateTicketTrigger.value.trigger({
      ticket: cloneDeep(detailData.value),
      newStatus,
    })

    if (result && result.statusCode === 200) {
      // Mettre à jour localement le statut affiché
      detailData.value = { ...detailData.value, status: newStatus }
      Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
        title: 'Succès',
        body: 'Statut du ticket mis à jour',
      })
      Events.dispatch('refresh_tickets')
    } else {
      Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
        title: 'Erreur',
        body: result?.message || 'Impossible de mettre à jour le statut',
      })
    }
  } catch {
    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
      title: 'Erreur',
      body: 'Impossible de mettre à jour le statut du ticket',
    })
  } finally {
    isUpdatingStatus.value = false
  }
}

const handleAddMessage = async () => {
  if (!newMessage.value.trim() || !detailData.value) return
  
  isSendingMessage.value = true
  try {
    const result = await addMessageTrigger.value.trigger({
      ticketId: detailData.value.id,
      userId: detailData.value.userId,
      subject: detailData.value.subject || detailData.value.title,
      text: newMessage.value.trim()
    })
    
    if (result && result.statusCode === 200) {
      if (result.data) {
        detailData.value = result.data
      } else {
        Events.dispatch('refresh_tickets')
      }
      newMessage.value = ''
      Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
        title: 'Succès',
        body: 'Message envoyé avec succès',
      })
    }
  } catch {
    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
      title: 'Erreur',
      body: 'Impossible d\'envoyer le message',
    })
  } finally {
    isSendingMessage.value = false
  }
}
</script>

<template>
  <div class="p-5 h-full overflow-auto bg-gray-50">
    <!-- Header -->
    <div class="mb-6">
      <h1 class="text-3xl font-bold text-gray-800 mb-2">Gestion des Tickets</h1>
      <p class="text-gray-600">Suivez et gérez les tickets de support client</p>
    </div>

    <!-- Tickets Table -->
    <TableList
      title="Tickets"
      dataKey="tickets"
      hasSearch
      :cols="cols"
      :refresh-on-events="['refresh_tickets']"
      @row-click="handleRowClick"
    ></TableList>

    <!-- Ticket Detail Slide View -->
    <SlideDetailView :isVisible="viewTicket" @hide="viewTicket = false">
      <div v-if="detailData">
        <div class="flex justify-between items-start mb-6">
          <div>
            <h2 class="text-2xl font-bold text-gray-800">{{ detailData.subject || detailData.title }}</h2>
            <p class="text-sm text-gray-500">Ticket ID: {{ detailData.id }}</p>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-6 mb-8">
          <div class="p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
            <p class="text-xs text-gray-400 uppercase font-bold mb-1">Émetteur</p>
            <!-- {{detailData}} -->
            <p class="font-semibold text-gray-700">{{ detailData.userName || detailData.user?.fullName || detailData.profile?.name || detailData.customerName || 'Inconnu' }}</p>
            <p class="text-xs text-gray-500">{{ detailData.userRole || 'Client' }}</p>
          </div>
        </div>

        <div class="mb-8">
          <h3 class="text-lg font-bold text-gray-800 mb-3 border-b pb-2">Conversation</h3>
          
          <!-- Message Thread -->
          <div class="space-y-4 mb-6 max-h-[400px] overflow-y-auto p-2">
            <!-- Initial Message -->
            <div class="flex flex-col items-start">
              <div class="max-w-[85%] p-4 bg-gray-100 rounded-2xl rounded-tl-none shadow-sm text-gray-700">
                <p class="text-xs font-bold text-gray-500 mb-1 uppercase tracking-tighter">{{ detailData.userName || detailData.user?.fullName || detailData.profile?.name || detailData.customerName || 'Émetteur' }}</p>
                <div class="whitespace-pre-wrap leading-relaxed">{{ detailData.message || detailData.description }}</div>
                <p class="text-[10px] text-gray-400 mt-2 text-right">{{ Commons.time(detailData.createdAt, 'DD/MM HH:mm') }}</p>
              </div>
            </div>

            <!-- Follow-up Messages -->
            <div 
              v-for="(msg, idx) in detailData.messages" 
              :key="idx"
              class="flex flex-col"
              :class="msg.senderRole === 'admin' ? 'items-end' : 'items-start'"
            >
              <div 
                class="max-w-[85%] p-4 rounded-2xl shadow-sm text-sm"
                :class="msg.senderRole === 'admin' ? 'bg-blue-600 text-white rounded-tr-none' : 'bg-gray-100 text-gray-700 rounded-tl-none'"
              >
                <p 
                  class="text-[10px] font-bold mb-1 uppercase tracking-tighter"
                  :class="msg.senderRole === 'admin' ? 'text-blue-100' : 'text-gray-500'"
                >
                  {{ msg.senderName || (msg.senderRole === 'admin' ? 'Support' : 'Émetteur') }}
                </p>
                <div class="whitespace-pre-wrap leading-relaxed">{{ msg.text }}</div>
                <p 
                  class="text-[10px] mt-2 text-right"
                  :class="msg.senderRole === 'admin' ? 'text-blue-200' : 'text-gray-400'"
                >
                  {{ Commons.time(msg.createdAt, 'DD/MM HH:mm') }}
                </p>
              </div>
            </div>
          </div>

          <!-- New Message Input -->
          <div v-if="detailData.status !== 'closed'" class="mt-4 p-4 bg-blue-50 rounded-2xl border border-blue-100">
            <h4 class="text-xs font-bold text-blue-800 mb-2 uppercase">Répondre au ticket</h4>
            <div class="flex gap-2">
              <textarea 
                v-model="newMessage"
                class="flex-1 p-3 rounded-xl border border-blue-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm resize-none"
                placeholder="Écrivez votre réponse ici..."
                rows="2"
                @keyup.enter.ctrl="handleAddMessage"
              ></textarea>
              <button 
                @click="handleAddMessage"
                :disabled="isSendingMessage || !newMessage.trim()"
                class="px-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shrink-0"
              >
                <i v-if="isSendingMessage" class="pi pi-spin pi-spinner mr-2"></i>
                <i v-else class="pi pi-send"></i>
              </button>
            </div>
            <p class="text-[10px] text-blue-400 mt-2">Ctrl + Entrée pour envoyer</p>
          </div>
          <div v-else class="mt-4 p-4 bg-gray-50 rounded-2xl border border-gray-200 text-center italic text-sm text-gray-400">
            Ce ticket est fermé. Réouvrez-le pour envoyer un message.
          </div>
        </div>

        <div v-if="detailData.metadata && Object.keys(detailData.metadata).length > 0" class="mb-8">
          <h3 class="text-lg font-bold text-gray-800 mb-3 border-b pb-2">Informations complémentaires</h3>
          <div class="bg-gray-50 rounded-xl p-4 font-mono text-sm overflow-auto">
            <pre>{{ JSON.stringify(detailData.metadata, null, 2) }}</pre>
          </div>
        </div>

        <!-- Mise à jour du statut — direct, sans modal intermédiaire -->
        <div class="space-y-4 pt-4 border-t">
          <h3 class="text-md font-bold text-gray-800 mb-2 uppercase tracking-wider text-xs">Mise à jour du statut</h3>
          <div v-if="isUpdatingStatus" class="flex items-center gap-2 text-sm text-gray-500">
            <i class="pi pi-spin pi-spinner"></i> Mise à jour en cours...
          </div>
          <div v-else class="flex flex-wrap gap-3">
            <button 
              v-if="detailData.status !== 'pending' && detailData.status !== 'resolved' && detailData.status !== 'closed'"
              @click="handleStatusChangeFromDetail('pending')"
              class="px-4 py-2 bg-yellow-500 text-white rounded-lg font-medium hover:bg-yellow-600 transition-colors flex items-center gap-2"
            >
              <i class="pi pi-play"></i> En cours
            </button>
            <button 
              v-if="detailData.status !== 'resolved' && detailData.status !== 'closed'"
              @click="handleStatusChangeFromDetail('resolved')"
              class="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <i class="pi pi-check"></i> Résoudre
            </button>
            <button 
              v-if="detailData.status !== 'closed'"
              @click="handleStatusChangeFromDetail('closed')"
              class="px-4 py-2 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              <i class="pi pi-times-circle"></i> Fermer
            </button>
            <button 
              v-if="detailData.status === 'closed' || detailData.status === 'resolved'"
              @click="handleStatusChangeFromDetail('open')"
              class="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <i class="pi pi-undo"></i> Réouvrir
            </button>
          </div>
        </div>
      </div>
    </SlideDetailView>

    <!-- Action Triggers -->
    <ActionTrigger
      dataKey="updateTicket"
      ref="updateTicketTrigger"
    />

    <ActionTrigger
      dataKey="addTicketMessage"
      ref="addMessageTrigger"
    />

    <ActionTrigger
      dataKey="createTicket"
      ref="createTicket"
      @success="detailData = null"
      @failed="
        err => {
          Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
            title: 'Ticket creation failed',
            body: `${err.result?.message || 'Unknown error occurred'}`,
          })
        }
      "
    />
  </div>
</template>
